import { Alert, Button } from "@heroui/react";
import { LockIcon } from "@phosphor-icons/react";

import { useBoundStore } from "@/app/store";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { EncryptionSetupProgress } from "./EncryptionSetupProgress";
import { EncryptionSetupForm } from "./EncryptionSetupForm";
import { useShallow } from "zustand/shallow";
import { useSetupEncryptionForm } from "../hooks/useSetupEncryptionForm";
import { useSetupEncryption } from "../hooks/useSetupEncryption";

interface SetupEncryptionProps {
  id: string;
  externalState: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

export function ManageEncryption({ id, externalState }: SetupEncryptionProps) {
  const { formValues, handleChange, resetForm, errors, validateForm } = useSetupEncryptionForm();
  const { isSetup, progress, progressMessage, handleSetupEncryption } = useSetupEncryption();

  const { deviceType } = useBoundStore(useShallow((state) => ({ deviceType: state.device.type })));
  const { appSettings } = useBoundStore(useShallow((state) => ({ appSettings: state.settings.app })));
  const isEncryptionEnabled = appSettings.encryption;

  const handleOpenChangeWithReset = (open: boolean) => {
    if (!open && !isSetup) {
      resetForm();
    }
    externalState.onOpenChange(open);
  };

  const handleSetupEncryptionClick = async () => {
    if (!validateForm()) return;
    await handleSetupEncryption(formValues.password);
    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    externalState.onOpenChange(false);
  };

  return (
    <DrawerAndModal
      actionsDisabled={isSetup}
      id={id}
      icon={<LockIcon />}
      isDismissable={isEncryptionEnabled}
      title={isEncryptionEnabled ? "Encryption Active" : "Setup End-to-End Encryption"}
      closeLabel={isEncryptionEnabled ? "Close" : "Cancel"}
      confirmLabel={isEncryptionEnabled ? undefined : "Setup Encryption"}
      onConfirm={isEncryptionEnabled ? undefined : handleSetupEncryptionClick}
      externalState={{ isOpen: externalState.isOpen, onOpenChange: handleOpenChangeWithReset }}
    >
      {isEncryptionEnabled ? (
        <div className="flex flex-col gap-4">
          <Alert
            color="primary"
            title="Encryption is active"
            description="Your data is protected with end-to-end encryption. All your notes, todos, tags, and folders are encrypted with your master password."
          />
          {deviceType === "mobile" && (
            <Button color="primary" onPress={handleCancel}>
              Close
            </Button>
          )}
        </div>
      ) : (
        <>
          {isSetup ? (
            <EncryptionSetupProgress progress={progress} progressMessage={progressMessage} />
          ) : (
            <EncryptionSetupForm
              formValues={formValues}
              errors={errors}
              isDisabled={isSetup}
              onPasswordChange={(value) => handleChange("password", value)}
              onConfirmPasswordChange={(value) => handleChange("confirmPassword", value)}
              onAcknowledgedChange={(value) => handleChange("acknowledged", value)}
              onAcknowledgedLimitedFunctionalityChange={(value) =>
                handleChange("acknowledgedLimitedFunctionality", value)
              }
            />
          )}

          {deviceType === "mobile" && (
            <div className="flex gap-2 pt-2">
              <Button variant="flat" isDisabled={isSetup} fullWidth onPress={handleCancel}>
                Cancel
              </Button>

              <Button color="primary" isDisabled={isSetup} fullWidth onPress={handleSetupEncryptionClick}>
                Setup
              </Button>
            </div>
          )}
        </>
      )}
    </DrawerAndModal>
  );
}
